import { useState, useEffect, useRef, useCallback } from "react";
import Button from "@mui/material/Button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useParams } from "react-router";
import styles from "styles/session-therapies.module.css";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingButton from "@mui/lab/LoadingButton";
import DoneIcon from "@mui/icons-material/Done";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import EnrollmentService from "services/EnrollmentService.js";
import Box from "@mui/material/Box";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import useAPI from "hooks/useAPI.js";
import PopUpModal from "components/utils/PopUpModal.js";
import { useOutletContext } from "react-router-dom";
import GoalLevelDisplay from "components/utils/GoalLevelDisplay";
import PatientService from "services/PatientService.js";
import { useLocation } from "react-router-dom";
import AutosaveIndicator from "components/utils/AutosaveIndicator";
import { useSlashCommands } from "hooks/useSlashCommands";
import SlashCommandDropdown from "components/inputs/SlashCommandDropdown";
// Define the desired order of therapies
const desiredOrder = [
  "Palmer",
  "Rooting",
  "Snout",
  "Babinski",
  "ATNR",
  "STNR",
  "TLR",
  "Moro",
  "Spinal Galant",
  "Supine",
  "Lateral",
  "Prone",
  "Push Ups",
  "Pull Ups",
  "Curl Ups",
  "Brachiation",
  "Balance",
  "Vestibular",
  "Smell",
  "Occulomotor",
  "MetroTimer",
  "TENS",
  "Transcranial",
  "Eyelights",
  "PONS",
  "Interactive Metronome",
  "VR / Video Games",
  "Laser"
];

const protocolCharts = [
  { therapyTypeName: "Palmer", imageSrc: "palmer.png" },
  { therapyTypeName: "Rooting", imageSrc: "rooting-snout.png" },
  { therapyTypeName: "Babinski", imageSrc: "babinski.png" },
  { therapyTypeName: "ATNR", imageSrc: "atnr.png" },
  { therapyTypeName: "STNR", imageSrc: "stnr.png" },
  { therapyTypeName: "Spinal Galant", imageSrc: "spinal-galant.png" },
  { therapyTypeName: "TLR", imageSrc: "tlr.png" },
  { therapyTypeName: "Moro", imageSrc: "moro.png" },
  { therapyTypeName: "Balance", imageSrc: "balance.png" },
  { therapyTypeName: "Supine", imageSrc: "supine.png" },
  { therapyTypeName: "Lateral", imageSrc: "lateral.png" },
  { therapyTypeName: "Prone", imageSrc: "prone.png" },
  { therapyTypeName: "Push Ups", imageSrc: "push-ups.png" },
  { therapyTypeName: "Pull Ups", imageSrc: "pull-ups.png" },
  { therapyTypeName: "Vestibular", imageSrc: "vestibular-exercises.png" },
  { therapyTypeName: "Smell", imageSrc: "smells.png" },
  { therapyTypeName: "Occulomotor", imageSrc: "occulomotor.png" },
  { therapyTypeName: "MetroTimer", imageSrc: "metrotimer.png" },
  {
    therapyTypeName: "Transcranial",
    imageSrc: "brain-driver.png",
  },
  {
    therapyTypeName: "Eyelights",
    imageSrc: "eye-lights.png",
  },
  {
    therapyTypeName: "PONS",
    imageSrc: "pons.png",
  },
  {
    therapyTypeName: "Interactive Metronome",
    imageSrc: "interactive-metronome.png",
  },
  { therapyTypeName: "VR / Video Games", imageSrc: "vr-or-video-games.png" },
  { therapyTypeName: "Brachiation", imageSrc: "pull-ups.png" },
  { therapyTypeName: "Curl Ups", imageSrc: "curl-ups.png" },
  { therapyTypeName: "Snout", imageSrc: "rooting-snout.png" },
  { therapyTypeName: "Laser", imageSrc: "lhd-or-rho.png" },
];

function getHtmlFileNameForTherapy(therapyTypeName) {
  const mapping = {
    "Primitive Reflex Chart": "primitive-reflex-simulation.html",
    Palmer: "palmer.html",
    Rooting: "rooting-snout.html",
    Snout: "rooting-snout.html",
    Babinski: "babinski.html",
    ATNR: "atnr.html",
    STNR: "stnr.html",
    "Spinal Galant": "spinal-galant.html",
    TLR: "tlr.html",
    Moro: "moro.html",
    Balance: "balance.html",
    Supine: "supine.html",
    Lateral: "lateral.html",
    Prone: "prone.html",
    "Push Ups": "push-ups.html",
    "Pull Ups": "pull-ups.html",
    "Curl Ups": "curl-ups.html",
    Vestibular: "vestibular-exercises.html",
    Smell: "olfactory.html",
    Occulomotor: "occulomotor.html",
    MetroTimer: "metrotimer.html",
    TENS: "tens-unit.html",
    Transcranial: "brain-driver.html",
    Eyelights: "eye-lights.html",
    PONS: "pons.html",
    "Interactive Metronome": "interactive-metronome-therapy.html",
    Neurosage: "neurosage.html",
    "VR / Video Games": "vr-or-video-games.html",
    Laser: "laser.html"
  };
  return mapping[therapyTypeName] || null;
}

function prepareProtocolChartHtml(html) {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const styles = Array.from(doc.querySelectorAll("style"))
      .map((el) =>
        el.textContent
          .replace(/body\s*\{[^}]*\}/gi, "")
          .replace(/\bbody\b/g, "#chart-container")
      )
      .filter(Boolean)
      .join("\n");
    const bodyHtml = doc.body?.innerHTML?.trim();
    if (!bodyHtml) return html;
    return styles ? `<style>${styles}</style>${bodyHtml}` : bodyHtml;
  } catch {
    return html;
  }
}

function formatBrainProgramLabel(metadata) {
  const programs = metadata?.brain_programs;
  if (!Array.isArray(programs) || programs.length === 0) return null;
  return programs.join(" · ");
}

function triggerGoogleTranslate() {
  const select = document.querySelector(".goog-te-combo");
  if (select) {
    const event = new Event("change", { bubbles: true });
    select.dispatchEvent(event);
  }
}


function SessionTherapies() {
  let navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCompleted = searchParams.get("isCompleted");

  const popUpModalRef = useRef(null);

  const [hospitalId] = useOutletContext();
  const { patientId } = useParams();
  const { enrollmentId } = useParams();
  const { sessionId } = useParams();

  const [sessionTherapies, setSessionTherapies] = useState([]);
  const [sessionsTherapiesHistory, setSessionsTherapiesHistory] = useState([]);
  const location = useLocation();
  const sessionNumber = location.state?.sessionNumber;

  const [patientInfo, setPatientInfo] = useState({ dob: null });
  const [protocolChartHtml, setProtocolChartHtml] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [brainProgramLabel, setBrainProgramLabel] = useState(null);

  const getSessionTherapiesAPI = useAPI(
    EnrollmentService.getSessionTherapies,
    getSessionTherapiesHandler
  );
  const getSessionsTherapiesHistoryAPI = useAPI(
    EnrollmentService.getSessionsTherapiesHistory,
    getSessionsTherapiesHistoryHandler
  );
  const saveSessionAPI = useAPI(EnrollmentService.saveSessionTherapy, () => {
    setLastSavedAt(Date.now());
  });
  const submitSessionAPI = useAPI(
    EnrollmentService.submitEnrollmentSession,
    submitSessionHandler,
    true
  );

  function getSessionTherapies() {
    getSessionTherapiesAPI.execute(
      hospitalId,
      patientId,
      enrollmentId,
      sessionId
    );
  }

  function getSessionsTherapiesHistory() {
    getSessionsTherapiesHistoryAPI.execute(
      hospitalId,
      patientId,
      enrollmentId,
      sessionId
    );
  }
  const getPatientInfoAPI = useAPI(
    PatientService.getPatient,
    getPatientInfoHandler
  );
  const getEnrollmentSessionAPI = useAPI(
    EnrollmentService.getEnrollmentSession,
    (response) => {
      setBrainProgramLabel(
        formatBrainProgramLabel(response.data?.metadata ?? null)
      );
    }
  );

  function getPatientInfoHandler(response) {
    setPatientInfo({
      name: response.data.firstName + " " + response.data.lastName,
      dob: response.data.dob,
    });
  }


  function saveSession(sessionTherapy) {
    saveSessionAPI.execute(hospitalId, patientId, enrollmentId, sessionId, {
      enrollmentTherapyTypeName: sessionTherapy.enrollmentTherapyTypeName,
      score: sessionTherapy.score,
      intensity: sessionTherapy.intensity,
      level: sessionTherapy.level,
      levelOverrideReason: sessionTherapy.levelOverrideReason,
      seconds: sessionTherapy.seconds,
      coachComment: sessionTherapy.coachComment,
      isCompleted: sessionTherapy.isCompleted,
    });
  }

  function submitEnrollmentSession() {
    submitSessionAPI.execute(hospitalId, patientId, enrollmentId, sessionId);
  }

  function getHistoryOfTherapy(therapyTypeName) {
    let result = sessionsTherapiesHistory.filter((history) => {
      if (history.length === 0) return false;
      return history[0].enrollmentTherapyTypeName === therapyTypeName;
    })
    if (result.length === 0) return [];
    return result[0]
  }


  useEffect(() => {
    getPatientInfoAPI.execute(hospitalId, patientId);
    getEnrollmentSessionAPI.execute(
      hospitalId,
      patientId,
      enrollmentId,
      sessionId
    );
    getSessionTherapies();
    getSessionsTherapiesHistory();
  }, []);

  /* API Success Handlers */

  function getSessionTherapiesHandler(response) {
    let data = response.data;

    // Sort the data based on the desired order
    data.sort((a, b) => {
      return (
        desiredOrder.indexOf(a.enrollmentTherapyTypeName) -
        desiredOrder.indexOf(b.enrollmentTherapyTypeName)
      );
    });

    setSessionTherapies(data);
  }

  function getSessionsTherapiesHistoryHandler(response) {
    setSessionsTherapiesHistory(response.data);
  }

  function submitSessionHandler(response) {
    navigate(-1);
  }

  async function viewProtocolChart(therapyName) {
    const file = getHtmlFileNameForTherapy(therapyName);

    if (!file) return;

    const path = `/user-forms/doctor-assessment/protocol-charts/html-charts/${file}`;

    try {
      const response = await fetch(path);
      const html = await response.text();
      const prepared = prepareProtocolChartHtml(html);
      setProtocolChartHtml(prepared);
      popUpModalRef.current.openModal();

      requestAnimationFrame(() => {
        const target = document.querySelector("#chart-container");
        if (target) target.innerHTML = prepared;
      });

      setTimeout(() => {
        triggerGoogleTranslate();

        const translated = document.querySelector("#translate-preload");
        const target = document.querySelector("#chart-container");
        if (translated && target) {
          target.innerHTML = prepareProtocolChartHtml(translated.innerHTML);
        }
      }, 1000);
    } catch {
      setProtocolChartHtml("<p>Chart not available.</p>");
      popUpModalRef.current.openModal();
    }
  }

  return (
    <Paper elevation={0} className={`surface-card ${styles["therapy-page"]}`}>
      <div className={styles["therapy-top-bar"]}>
        <Button
          onClick={() => navigate(-1)}
          variant="text"
          size="small"
          className={styles["therapy-back-btn"]}
          startIcon={<ArrowBackIcon fontSize="small" />}
        >
          Back
        </Button>
        <div className={styles["therapy-title-block"]}>
          <h1 className={styles["therapy-page-title"]}>
            Session Therapies
            {sessionNumber ? (
              <span className={styles["therapy-session-tag"]}>
                Session #{sessionNumber}
              </span>
            ) : null}
          </h1>
          <p className={styles["therapy-page-subtitle"]}>
            Changes auto-save as you work
          </p>
        </div>
        <div className={styles["therapy-top-meta"]}>
          <GoalLevelDisplay
            patientDob={patientInfo.dob}
            className={styles["goal-level-badge"]}
          />
          <AutosaveIndicator
            status={saveSessionAPI.status}
            lastSavedAt={lastSavedAt}
          />
        </div>
      </div>
      <form>
        {getSessionTherapiesAPI.status === "pending" ? (
          <div className="text-center">
            <CircularProgress sx={{ my: 8 }} />
          </div>
        ) : (
          <>
            <div className={styles["therapy-toolbar"]}>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => viewProtocolChart("Primitive Reflex Chart")}
              >
                Primitive Reflex Chart
              </button>
              {brainProgramLabel ? (
                <div className={styles["therapy-brain-program"]}>
                  <span className={styles["therapy-brain-program-label"]}>
                    Brain program
                  </span>
                  <span className={styles["therapy-brain-program-value"]}>
                    {brainProgramLabel}
                  </span>
                </div>
              ) : null}
            </div>
            <div className={styles["therapy-layout"]}>
              <nav className={styles["therapy-nav"]} aria-label="Therapy sections">
                <p className={styles["therapy-nav-title"]}>Jump to</p>
                <div className={styles["therapy-nav-list"]}>
                  {sessionTherapies.map((therapy) => (
                    <Button
                      key={`nav-${therapy.id}`}
                      variant="text"
                      className={styles["therapy-nav-item"]}
                      onClick={() => {
                        const el = document.getElementById(
                          `therapy-section-${therapy.id}`
                        );
                        if (el) {
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                      }}
                    >
                      {therapy.enrollmentTherapyTypeName}
                    </Button>
                  ))}
                </div>
              </nav>
              <div className={styles["therapy-list"]}>
                {sessionTherapies.map((therapy) => (
                  <div id={`therapy-section-${therapy.id}`} key={therapy.id}>
                    <TherapyCard
                      therapy={therapy}
                      history={getHistoryOfTherapy(
                        therapy.enrollmentTherapyTypeName
                      )}
                      saveSessionHandler={saveSession}
                      viewProtocolChart={viewProtocolChart}
                    />
                  </div>
                ))}
              </div>
            </div>
            {isCompleted === "false" && (
              <div className={styles["therapy-submit-bar"]}>
                <p className={styles["therapy-submit-hint"]}>
                  Review all therapies before submitting this session.
                </p>
                <div className={styles["therapy-submit-actions"]}>
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  {submitSessionAPI.status === "pending" ? (
                    <LoadingButton
                      loading
                      loadingPosition="start"
                      startIcon={<SaveIcon />}
                      variant="contained"
                      size="small"
                    >
                      Submitting…
                    </LoadingButton>
                  ) : (
                    <Button
                      type="submit"
                      startIcon={<DoneIcon />}
                      variant="contained"
                      size="small"
                      onClick={submitEnrollmentSession}
                    >
                      Submit session
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </form>

      <div id="translate-preload" style={{ display: "none" }} dangerouslySetInnerHTML={{ __html: protocolChartHtml }}></div>

      <PopUpModal ref={popUpModalRef} modalWidth="900px" contentPadding={0}>
        <div id="chart-container" className={styles["chart-container"]} />
      </PopUpModal>
    </Paper>
  );
}

function TherapyCard({
  therapy,
  history,
  saveSessionHandler,
  viewProtocolChart,
  onEdited,
}) {
  const [therapyState, setTherapyState] = useState(therapy);
  const [isExpanded, setIsExpanded] = useState(true);

  const onInputChangeHandler = (e) => {
    let name = e.target.name.split("-")[0];
    let value = e.target.value;
    onEdited?.();
    setTherapyState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const setFieldValue = useCallback((name, value) => {
    onEdited?.();
    setTherapyState((prev) => ({ ...prev, [name]: value }));
  }, [onEdited]);

  const { getSlashProps, dropdownState, applyShortcut } = useSlashCommands(setFieldValue);

  useEffect(() => {
    if (therapy !== therapyState) saveSessionHandler(therapyState);
  }, [therapyState]);

  return (
    <div className={styles["therapy-card"]}>
      <div className={styles["therapy-header"]}>
        <div className={styles["therapy-header-main"]}>
          <h2 className={styles["therapy-title"]}>
            {therapy.enrollmentTherapyTypeName}
          </h2>
          {protocolCharts.some(
            (obj) => obj.therapyTypeName === therapy.enrollmentTherapyTypeName
          ) ? (
            <button
              type="button"
              className={`btn btn-outline-secondary btn-sm ${styles["therapy-chart-btn"]}`}
              onClick={() =>
                viewProtocolChart(therapy.enrollmentTherapyTypeName)
              }
            >
              Chart
            </button>
          ) : null}
        </div>
        <Button
          variant="text"
          size="small"
          className={styles["therapy-collapse-btn"]}
          onClick={() => setIsExpanded((prev) => !prev)}
          endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {isExpanded ? "Hide" : "Show"}
        </Button>
      </div>
      {isExpanded && (
        <>
          <div className={styles["therapy-form-primary"]}>
            <div
              className={`${styles["therapy-field"]} ${styles["therapy-field-score"]}`}
            >
              {["TENS", "Laser", "Transcranial", "Eyelights"].includes(
                therapy.enrollmentTherapyTypeName
              ) ? (
                <div className={styles["therapy-completed"]}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`completedInput-${therapy.id}`}
                    name={`completed-${therapy.id}`}
                    checked={therapyState.isCompleted === true}
                    onChange={(e) => {
                      onEdited?.();
                      setTherapyState((prevState) => ({
                        ...prevState,
                        isCompleted: e.target.checked,
                      }));
                    }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`completedInput-${therapy.id}`}
                  >
                    Completed
                  </label>
                </div>
              ) : (
                <>
                  <span className={styles["therapy-field-label"]}>Score</span>
                  <div className={styles["score-group"]}>
                    <div className={styles["score-input"]}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name={"score-" + therapy.id}
                        id={"scoreInput-" + therapy.id + "-Plus"}
                        value="+"
                        onChange={onInputChangeHandler}
                        checked={therapyState.score === "+"}
                      />
                      <label htmlFor={"scoreInput-" + therapy.id + "-Plus"}>
                        +
                      </label>
                    </div>
                    <div className={styles["score-input"]}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name={"score-" + therapy.id}
                        id={"scoreInput-" + therapy.id + "-Minus"}
                        value="-"
                        onChange={onInputChangeHandler}
                        checked={therapyState.score === "-"}
                      />
                      <label htmlFor={"scoreInput-" + therapy.id + "-Minus"}>
                        -
                      </label>
                    </div>
                    <div className={styles["score-input"]}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name={"score-" + therapy.id}
                        id={"scoreInput-" + therapy.id + "-Skip"}
                        value="S"
                        onChange={onInputChangeHandler}
                        checked={therapyState.score === "S"}
                      />
                      <label htmlFor={"scoreInput-" + therapy.id + "-Skip"}>
                        Skip
                      </label>
                    </div>
                  </div>
                </>
              )}
            </div>
            {therapy.enrollmentTherapyTypeName === "Transcranial" && (
              <div
                className={`${styles["therapy-field"]} ${styles["therapy-field-intensity"]}`}
              >
                <label
                  htmlFor={"intensityInput-" + therapy.id}
                  className={styles["therapy-field-label"]}
                >
                  Intensity
                </label>
                <select
                  className="form-select"
                  name={"intensity-" + therapy.id}
                  id={"intensityInput-" + therapy.id}
                  onChange={onInputChangeHandler}
                  value={therapyState.intensity}
                >
                  <option value="0">0</option>
                  <option value="0.5">0.5</option>
                  <option value="1">1</option>
                  <option value="1.5">1.5</option>
                  <option value="2">2</option>
                </select>
              </div>
            )}
            <div
              className={`${styles["therapy-field"]} ${styles["therapy-level-highlight"]}`}
            >
              <label
                htmlFor={"levelInput-" + therapy.id}
                className={styles["therapy-level-label"]}
              >
                Level
              </label>
              <select
                className={`form-select ${styles["therapy-level-select"]}`}
                name={"level-" + therapy.id}
                id={"levelInput-" + therapy.id}
                onChange={onInputChangeHandler}
                value={therapyState.level}
              >
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles["therapy-form-details"]}>
            <div
              className={`${styles["therapy-field"]} ${styles["therapy-field-reason"]}`}
            >
              <label
                htmlFor={"levelOverrideReasonInput-" + therapy.id}
                className={styles["therapy-field-label"]}
              >
                Override reason
              </label>
              <input
                type="text"
                name={"levelOverrideReason-" + therapy.id}
                className="form-control"
                id={"levelOverrideReasonInput-" + therapy.id}
                value={
                  therapyState.levelOverrideReason !== null
                    ? therapyState.levelOverrideReason
                    : ""
                }
                {...getSlashProps("levelOverrideReason", onInputChangeHandler)}
              />
            </div>
            <div
              className={`${styles["therapy-field"]} ${styles["therapy-field-seconds"]}`}
            >
              <label
                htmlFor={"secondsInput-" + therapy.id}
                className={styles["therapy-field-label"]}
              >
                Seconds
              </label>
              <input
                type="number"
                name={"seconds-" + therapy.id}
                className="form-control"
                id={"secondsInput-" + therapy.id}
                onChange={onInputChangeHandler}
                value={
                  therapyState.seconds !== null ? therapyState.seconds : ""
                }
              />
            </div>
            <div
              className={`${styles["therapy-field"]} ${styles["therapy-field-comment"]}`}
            >
              <label
                htmlFor={"coachCommentInput-" + therapy.id}
                className={styles["therapy-field-label"]}
              >
                Coach comment
              </label>
              <select
                className="form-select"
                name={"coachComment-" + therapy.id}
                id={"coachCommentInput-" + therapy.id}
                onChange={onInputChangeHandler}
                value={
                  therapyState.coachComment !== null
                    ? therapyState.coachComment
                    : ""
                }
              >
                <option value=""></option>
                <option value="Late">Late</option>
                <option value="Extra Assistance/Improper Form">
                  Extra Assistance/Improper Form
                </option>
                <option value="Equipment Failure">Equipment Failure</option>
                <option value="Change of Coach/Time Slot">
                  Change of Coach/Time Slot
                </option>
                <option value="New Partner">New Partner</option>
                <option value="Modified Protocal">Modified Protocal</option>
                <option value="Refusal To Perform">Refusal To Perform</option>
                <option value="Distractive/Hyperactive">
                  Distractive/Hyperactive
                </option>
                <option value="Avoidance">Avoidance</option>
              </select>
            </div>
          </div>
          {history === undefined || history.length === 0 ? null : (
            <div className={styles["history-container"]}>
                <div className={styles["history-header"]}>
                  <p className={styles["history-header-title"]}>Session History</p>
                  <span className={styles["history-session-count"]}>
                    {history.length} sessions
                  </span>
                </div>
                <div className={styles["history-legend"]}>
                  <span className={styles["history-legend-item"]}>
                    <span className={`${styles["history-legend-swatch"]} ${styles["history-legend-swatch-plus"]}`}>+</span>
                    Positive
                  </span>
                  <span className={styles["history-legend-item"]}>
                    <span className={`${styles["history-legend-swatch"]} ${styles["history-legend-swatch-minus"]}`}>-</span>
                    Negative
                  </span>
                  <span className={styles["history-legend-item"]}>
                    <span className={`${styles["history-legend-swatch"]} ${styles["history-legend-swatch-skip"]}`}>S</span>
                    Skip
                  </span>
                  <span className={styles["history-scroll-hint"]}>Scroll for earlier sessions →</span>
                </div>
                <div className={styles["history-panel"]}>
                  <div className={styles["history-labels-col"]}>
                    <Typography
                      color="text.default"
                      variant="subtitle2"
                      component="span"
                      className={styles["history-sub-label"]}
                    >
                      Session
                    </Typography>
                    <Typography
                      color="text.default"
                      variant="subtitle2"
                      component="span"
                      className={styles["history-sub-label"]}
                    >
                      Score
                    </Typography>
                    {therapy.enrollmentTherapyTypeName === "Transcranial" && (
                      <Typography
                        color="text.default"
                        variant="subtitle2"
                        component="span"
                        className={styles["history-sub-label"]}
                      >
                        Intensity
                      </Typography>
                    )}
                    <Typography
                      color="text.default"
                      variant="subtitle2"
                      component="span"
                      className={styles["history-sub-label"]}
                    >
                      Level
                    </Typography>
                  </div>
                  <div className={styles["history-scroll-wrap"]}>
                    <div className={styles["history-scroll-fade"]} aria-hidden="true" />
                    <div className={styles["history-scroll-area"]}>
                      <div className={styles["history-scroll-content"]}>
                        <Stack
                          direction="row"
                          spacing={0}
                          className={styles["history-values-row"]}
                        >
                          {history.map((sessionTherapy, index) => {
                            return (
                              <Typography
                                key={"session-history-" + index}
                                component="span"
                                className={`${styles["history-cell"]} ${styles["history-session-val"]}`}
                              >
                                {index + 1}
                              </Typography>
                            );
                          })}
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={0}
                          className={styles["history-values-row"]}
                        >
                          {history.map((sessionTherapy, index) => {
                            return (
                              <span key={"score-history-" + index} className={styles["history-cell"]}>
                                <Box
                                  component="span"
                                  className={styles["history-score-val"]}
                                  sx={{
                                    backgroundColor: getScoreBgColor(sessionTherapy.score),
                                  }}
                                >
                                  <Typography color="white" component="span">
                                    {sessionTherapy.score !== null
                                      ? sessionTherapy.score
                                      : ""}
                                  </Typography>
                                </Box>
                              </span>
                            );
                          })}
                        </Stack>
                        {therapy.enrollmentTherapyTypeName === "Transcranial" && (
                          <Stack
                            direction="row"
                            spacing={0}
                            className={styles["history-values-row"]}
                          >
                            {history.map((sessionTherapy, index) => {
                              return (
                                <Typography
                                  key={"intensity-history-" + index}
                                  component="span"
                                  className={`${styles["history-cell"]} ${styles["history-intensity-val"]}`}
                                >
                                  {sessionTherapy.intensity}
                                </Typography>
                              );
                            })}
                          </Stack>
                        )}
                        <Stack
                          direction="row"
                          spacing={0}
                          className={styles["history-values-row"]}
                        >
                          {history.map((sessionTherapy, index) => {
                            return (
                              <span key={"level-history-" + index} className={styles["history-cell"]}>
                                <Typography
                                  component="span"
                                  className={styles["history-level-val"]}
                                >
                                  {sessionTherapy.level}
                                </Typography>
                              </span>
                            );
                          })}
                        </Stack>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
          )}
        </>
      )}
      <SlashCommandDropdown state={dropdownState} onSelect={applyShortcut} />
    </div>
  );
}

function getScoreBgColor(score) {
  if (score === "+") {
    return "success.main";
  } else if (score === "-") {
    return "error.main";
  } else if (score === "S") {
    return "primary.main";
  } else {
    return "white";
  }
}

export default SessionTherapies;